import {
  formatDurationClock,
  formatDurationShort,
  MARATHON_DISTANCE_KM,
  isMarathonWinner,
} from "@/utils/formatters";

interface MarathonProgressSectionProps {
  userAvatar: string;
  totalDistance: number;
  totalDuration: number; // seconds
  targetDurationSeconds: number | null;
  averagePace: string | null;
  calories?: number;
}

export function MarathonProgressSection({
  userAvatar,
  totalDistance,
  totalDuration,
  targetDurationSeconds,
  averagePace,
  calories = 0,
}: MarathonProgressSectionProps) {
  const distanceProgress = Math.min(
    (totalDistance / MARATHON_DISTANCE_KM) * 100,
    100
  );
  const timeProgress = targetDurationSeconds
    ? Math.min((totalDuration / targetDurationSeconds) * 100, 100)
    : 0;
  const isCompleted = totalDistance >= MARATHON_DISTANCE_KM;
  const isWithinTime = targetDurationSeconds
    ? totalDuration <= targetDurationSeconds
    : true;
  const isWinner = isMarathonWinner(
    totalDistance,
    totalDuration,
    targetDurationSeconds
  );

  return (
    <div className="p-4 pt-6">
      {/* Distance Progress Circle */}
      <div className="flex flex-col items-center text-center mb-6">
        <div className="relative size-40">
          <svg className="size-full -rotate-90" viewBox="0 0 36 36">
            <circle
              cx="18"
              cy="18"
              r="15.91549430918954"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              className="text-white/10"
            />
            <circle
              cx="18"
              cy="18"
              r="15.91549430918954"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeDasharray={`${distanceProgress}, 100`}
              strokeLinecap="round"
              className="text-primary"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-white">
              {totalDistance.toFixed(1)}
            </span>
            <span className="text-sm text-white/60">
              / {MARATHON_DISTANCE_KM} km
            </span>
          </div>
        </div>
        <p className="mt-4 text-base font-medium text-primary">
          Target: {formatDurationShort(targetDurationSeconds || 0)}
        </p>
      </div>

      {/* Marathon Stats Card */}
      <div className="rounded-4xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-4 border border-primary/40">
        {/* User Avatar & Title */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className="size-12 rounded-full bg-cover bg-center border-2 border-primary"
            style={{ backgroundImage: `url("${userAvatar}")` }}
          />
          <div>
            <h3 className="text-lg font-bold text-white">Your Marathon</h3>
            <p className="text-sm text-white/60">
              {isCompleted ? "Completed!" : "In Progress"}
            </p>
          </div>
        </div>

        {/* Time Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-white/60">Time</span>
            <span className="text-sm font-medium text-white">
              {formatDurationClock(totalDuration)} /{" "}
              {formatDurationClock(targetDurationSeconds || 0)}
            </span>
          </div>
          <div className="h-3 bg-white/10 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                isWithinTime ? "bg-primary" : "bg-red-500"
              }`}
              style={{ width: `${Math.min(timeProgress, 100)}%` }}
            />
          </div>
          {targetDurationSeconds && totalDuration < targetDurationSeconds && (
            <p className="text-xs text-primary mt-1">
              {formatDurationShort(targetDurationSeconds - totalDuration)}{" "}
              remaining
            </p>
          )}
          {targetDurationSeconds && totalDuration > targetDurationSeconds && (
            <p className="text-xs text-red-400 mt-1">
              Exceeded by{" "}
              {formatDurationShort(totalDuration - targetDurationSeconds)}
            </p>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/5 rounded-2xl p-3 text-center">
            <p className="text-2xl font-bold text-white">
              {averagePace ?? "--"}
            </p>
            <p className="text-xs text-white/60">Avg Pace</p>
          </div>
          <div className="bg-white/5 rounded-2xl p-3 text-center">
            <p className="text-2xl font-bold text-white">{calories || 0}</p>
            <p className="text-xs text-white/60">Calories</p>
          </div>
          <div className="bg-white/5 rounded-2xl p-3 text-center">
            <p className="text-2xl font-bold text-white">
              {isCompleted ? (isWinner ? "✓" : "✗") : "—"}
            </p>
            <p className="text-xs text-white/60">Winner</p>
          </div>
        </div>
      </div>
    </div>
  );
}
